import { VoiceCallRecordingDto } from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { summaryFormatter } from '@act/utilities/formatters/SummaryFormatter';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { SparkleIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { callsRequests } from 'redux/requests/calls.request';
import { useToast } from '../../../../app/ReusableToast';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { TrackingProvider } from '../../../../tracking';
import AccuracyQuestion from '../../../post-call-summary-drawer/post-call-feedback/AccuracyQuestion';
import NegativeQuestion from '../../../post-call-summary-drawer/post-call-feedback/NegativeQuestion';
import { INITIAL_POST_CALL_FEEDBACK_STATE } from '../../../post-call-summary-drawer/post-call-feedback/initialPostCallFeedbackState.const';
import { PostCallFeedbackState } from '../../../post-call-summary-drawer/post-call-feedback/types';
import { useMobile } from '@act/utilities/MobileContext';

export const Summary = ({
  callRecording,
}: {
  callRecording: VoiceCallRecordingDto;
}) => {
  const theme = useTheme();
  const toast = useToast();
  const { isMobile } = useMobile();
  const callDataQualityFF = useFeatureFlag('call-data-quality');

  const [postCallFeedback, setPostCallFeedback] =
    useState<PostCallFeedbackState>(
      callRecording?.isAccurateSummary
        ? INITIAL_POST_CALL_FEEDBACK_STATE
        : {
            ...INITIAL_POST_CALL_FEEDBACK_STATE,
            isAccurateSummary: callRecording?.isAccurateSummary,
            summaryAccuracyIssues: callRecording?.summaryAccuracyIssues || [],
            summaryAccuracyFeedback: callRecording?.summaryAccuracyFeedback,
          }
    );

  const [
    updatedVoiceCallRecording,
    isUpdatingTranscriptionFeedback,
    updateVoiceCallRecording,
  ] = useApiCall(callsRequests.updateVoiceCallRecording);

  async function submitFeedback(updatedFeedback?: PostCallFeedbackState) {
    const feedbackToSubmit = updatedFeedback || postCallFeedback;

    try {
      updateVoiceCallRecording({
        params: { id: callRecording.id },
        body: {
          isAccurateSummary: feedbackToSubmit.isAccurateSummary,
          summaryAccuracyIssues:
            feedbackToSubmit.summaryAccuracyIssues.length > 0
              ? feedbackToSubmit.summaryAccuracyIssues
              : undefined,
          summaryAccuracyFeedback: feedbackToSubmit.summaryAccuracyFeedback,
          isAccurateTranscription: undefined,
          accuracyIssues: undefined,
          accuracyFeedback: undefined,
        },
      });
      toast('Thank you for your feedback!', 'success');
    } catch (error) {
      toast('Failed to submit feedback. Please try again.', 'error');
    }
  }

  useEffect(() => {
    if (updatedVoiceCallRecording) {
      setPostCallFeedback({
        ...postCallFeedback,
        isAccurateSummary: updatedVoiceCallRecording.isAccurateSummary,
        summaryAccuracyIssues:
          updatedVoiceCallRecording?.summaryAccuracyIssues || [],
        summaryAccuracyFeedback:
          updatedVoiceCallRecording.summaryAccuracyFeedback,
      });
      toast('Thank you for your feedback!', 'success');
    }
  }, [updatedVoiceCallRecording]);

  const sourceData = updatedVoiceCallRecording || callRecording;
  const shouldShowSummaryFeedback =
    sourceData?.isAccurateSummary === null &&
    postCallFeedback.isAccurateSummary === null;

  const shouldShowNegativeFeedback =
    sourceData?.isAccurateSummary === null &&
    postCallFeedback.isAccurateSummary === false;

  const getSummaryHeight = () => {
    if (shouldShowNegativeFeedback) {
      return isMobile ? '80%' : '300px';
    }
    return isMobile ? '100%' : '400px';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Box sx={{ flex: 3, overflow: 'auto', minHeight: 0 }}>
        <FlexContainer
          borderRadius="lessRounded"
          direction="column"
          gap={4}
          padding={2}
          width="100%"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              flexDirection: 'row',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  height: getSummaryHeight(),
                  position: 'relative',
                  backgroundColor: theme.palette.neutralAccent.main,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.greyShades[250]}`,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 0,
                  flexShrink: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      marginBottom: 2,
                    }}
                  >
                    <SparkleIcon size={16} />
                    <Typography variant="bodySmall">Generated</Typography>
                  </Box>
                  {callRecording?.llmGeneratedNotes
                    ? summaryFormatter.markdownDisplayerCompact(
                        callRecording?.llmGeneratedNotes
                      )
                    : 'No generated notes available.'}
                </Box>
              </Box>
            </Box>
          </Box>
        </FlexContainer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'auto',
          minHeight: '20%',
          justifyContent: shouldShowNegativeFeedback
            ? 'flex-start'
            : 'flex-end',
        }}
      >
        <TrackingProvider contextName="post-call-feedback">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '8px 32px',
              border: callDataQualityFF ? 'none' : '1px solid #E0E0E0',
              boxSizing: 'border-box',
              width: '100%',
              flexGrow: shouldShowNegativeFeedback ? 1 : 0,
            }}
          >
            {isUpdatingTranscriptionFeedback && <CircularProgress />}

            {shouldShowSummaryFeedback && (
              <AccuracyQuestion
                feedbackType="summary"
                postCallFeedback={postCallFeedback}
                isUpdatingPostCallFeedback={isUpdatingTranscriptionFeedback}
                setPostCallFeedback={setPostCallFeedback}
                onClose={() => {}}
                submitFeedback={submitFeedback}
              />
            )}

            {shouldShowNegativeFeedback && (
              <Box sx={{ width: '100%' }}>
                <NegativeQuestion
                  feedbackType="summary"
                  postCallFeedback={postCallFeedback}
                  isUpdatingPostCallFeedback={isUpdatingTranscriptionFeedback}
                  setPostCallFeedback={setPostCallFeedback}
                  onClose={() =>
                    setPostCallFeedback(INITIAL_POST_CALL_FEEDBACK_STATE)
                  }
                  submitFeedback={submitFeedback}
                />
              </Box>
            )}
          </Box>
        </TrackingProvider>
      </Box>
    </Box>
  );
};
